import { useContext, useState } from 'react'
import { Drawer, Timeline, Form, Select, notification, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { documentBiltiesObjectFunction, documentOrdersMapFunction, momentDateDT, RBACSwitch } from '../../utils/Functions'
import Icon from '@mdi/react'
import { orderCategoryEnum, BiltyStatusEnum, LocationEnums, SegmentsEnum, ContainerDimensionsEnum, BiltyTrackingEnum } from '../../utils/Enums'
import { Outlet, useNavigate, useParams } from 'react-router-dom'
import {
	mdiClose,
	mdiFileCancelOutline,
	mdiFileDownloadOutline,
	mdiChevronDown,
	mdiAccount,
	mdiPhone,
	mdiClockTimeEightOutline,
	mdiTextBoxOutline,
	mdiPackageVariantClosed,
	mdiCreditCardOutline,
	mdiAlertCircleOutline,
	mdiVectorLink,
	mdiMenuDown,
	mdiVectorUnion,
	mdiReceipt,
	mdiFileEditOutline,
} from '@mdi/js'
import { LevelContext } from '../../services/LevelContext'
import { userAtom } from '../../recoil/atoms'
import { useRecoilValue } from 'recoil'
import { useMarkTrackingComplete } from '../../hooks/modalFinishHooks'

const { Item } = Timeline

const DrawerHeader = (props) => {
	const { record, tabStatus, orderType, collection, navigator } = props
	const user = useRecoilValue(userAtom)
	const orderCategory = [record?.orderCategory]?.flat()
	const lotOrder = record?.subOrders?.[0]?.preferences?.lotOrder
	const biltyCreated = record?.bilty?.[0]?.biltyCreatedAt
	const showCreatedBilty = lotOrder && !biltyCreated && tabStatus === 'inProgress'

	return (
		<>
			<div className='w-full flex flex-col px-7 py-5 border-b border-[rgba(0, 0, 0, 0.16)]'>
				<div className='flex flex-row justify-between items-center mb-0.5'>
					<div className='flex flex-row'>
						<span className='font-bold text-lg mr-3'>J-{record?.jobNumber}</span>
						<span className=''>
							{orderCategory?.some((value) => ['Shifting', 'Destuffing', 'DoubleTwenty']?.includes(value)) && (
								<div className={`order-type-pill ${orderCategoryEnum[orderCategory[0]]?.background}`}>{orderCategoryEnum[record?.orderCategory[0]]?.text}</div>
							)}
							{lotOrder && <div className='order-type-pill bg-yellow-300'>Lot Order</div>}
						</span>
					</div>
					<div className='cursor-pointer' onClick={() => navigator(-1)}>
						<Icon path={mdiClose} size={1} color='#676767' />
					</div>
				</div>
				<div className='font-semibold text-sm text-black-300 mb-0.5'>{record?.businessName?.[0]}</div>
				<div className='text-sm text-black-300 mb-4'>{record?.shippingLine?.[0]}</div>
				{collection !== 'biltyTracking' && (
					<div className='flex flex-row justify-start items-center'>
						<button
							className='flex flex-row items-center w-fit !h-10 btn-primary mr-4'
							onClick={(e) => {
								e.stopPropagation()
								navigator(
									`/biltyManagement/${orderType}/${tabStatus}/job/${record?._id}/sideDrawer/${
										BiltyStatusEnum[showCreatedBilty ? 'issuance' : tabStatus].biltyActionModal
									}`
								)
							}}
						>
							<Icon className='mr-2' size={1} path={BiltyStatusEnum[showCreatedBilty ? 'issuance' : tabStatus].biltyActionIcon} />
							<div className='!font-medium'>{BiltyStatusEnum[showCreatedBilty ? 'issuance' : tabStatus].biltyActionText}</div>
						</button>
						{record?.bilty?.[0]?.biltyType === 'systemGenerated' && (
							<div
								className='flex items-center justify-center h-10 w-10 rounded !bg-grey-200 mr-4 cursor-pointer'
								onClick={(e) => {
									e.stopPropagation()
									navigator(`/biltyManagement/${orderType}/${tabStatus}/job/${record?._id}/sideDrawer/printBilty`)
								}}
							>
								<Icon className='text-black-300' path={mdiFileDownloadOutline} size={1}></Icon>
							</div>
						)}
						{!lotOrder && tabStatus === 'inProgress' && user?.userType === 'BiltySuperAdmin' && (
							<div
								className='flex items-center justify-center h-10 w-10 rounded !bg-red-200 cursor-pointer'
								onClick={(e) => {
									e.stopPropagation()
									navigator(`/biltyManagement/${orderType}/${tabStatus}/job/${record?._id}/sideDrawer/cancelBilty`)
								}}
							>
								<Icon className='text-red-400' path={mdiFileCancelOutline} size={1}></Icon>
							</div>
						)}
					</div>
				)}
			</div>
		</>
	)
}

const RouteCollapse = (props) => {
	const { record } = props
	const journeyLocations = record?.journeyLocations
	const [expandedKey, setExpandedKey] = useState(false)

	return (
		<>
			<div className={`bg-primary-75 ${expandedKey ? 'collapse-animation-open' : 'collapse-animation-close'}`}>
				<div className='flex flex-col justify-end pl-6 pr-3 pt-6'>
					<Timeline className=''>
						{Object?.keys(journeyLocations || {})?.map((locationKey) =>
							journeyLocations?.[locationKey]?.map((location) => (
								<Item
									key={locationKey}
									dot={
										<div className={`p-1 !rounded-full ${LocationEnums?.[locationKey]?.color}`}>
											<Icon path={LocationEnums?.[locationKey]?.icon} size={0.6} />
										</div>
									}
								>
									<div className='flex flex-col items-start'>
										<div className='text-[10px] font-medium text-black-300'>{LocationEnums?.[locationKey]?.text}</div>
										<div className='text-xs w-[300px] font-semibold text-black-400 mb-2 truncate'>
											{location?.Location?.formattedAddress + ', ' + location?.Location?.additionalDetails}
										</div>
										{location?.contacts &&
											location?.contacts?.map((contact, index) => (
												<div key={index} className='flex flex-row items-center mb-1'>
													<div className='flex flex-row items-center mr-3'>
														<div className='bg-white rounded-full p-1 mr-1'>
															<Icon path={mdiAccount} size={0.5} color='#676767' />
														</div>
														<div className='text-xs text-black-300'>{contact?.name}</div>
													</div>
													<div className='flex flex-row items-center'>
														<div className='bg-white rounded-full p-1 mr-1'>
															<Icon path={mdiPhone} size={0.5} color='#676767' />
														</div>
														<div className='text-xs text-black-300'>{contact?.phoneNumber}</div>
													</div>
												</div>
											))}
									</div>
								</Item>
							))
						)}
					</Timeline>
				</div>
			</div>
			<div className='flex flex-row items-center h-10 !bg-white py-4 px-6' onClick={() => setExpandedKey((prevBool) => !prevBool)}>
				<div className='font-medium text-sm text-primary-800 mr-1'>{expandedKey ? 'Hide' : 'See'} Route Information</div>
				<Icon className={`transition-all !duration-1000 ${expandedKey ? 'rotate-180' : 'rotate-0'}`} path={mdiChevronDown} size={0.9} />
			</div>
		</>
	)
}

const BiltyInfoCard = (props) => {
	const { record, segment, collection, setChangedValues } = props
	const [form] = Form.useForm()
	const navigator = useNavigate()

	const user = useRecoilValue(userAtom)
	const [promiseLoading, setPromiseLoading] = useState({})
	const [finishFunction] = useMarkTrackingComplete()

	const handleChange = (changedValues) => {
		setChangedValues(changedValues)
		const biltyId = Object.keys(changedValues)?.[0]
		const changedBiltyStatus = Object.values(changedValues)?.[0]?.biltyStatus
		if (changedBiltyStatus === 'completed') {
			navigator(`/biltyTracking/${segment}/job/${record?._id}/sideDrawer/markTrackingComplete`)
		} else {
			form
				.validateFields()
				.then(async () => {
					setPromiseLoading({ [biltyId]: true })
					return await finishFunction({
						segment,
						props: {
							changedValues,
							data: {
								record: record,
							},
						},
					})
						.then((response) => response)
						.catch((e) => ({ type: 'error', message: 'Error', description: e.message }))
				})
				.then((queryresult) => {
					if (queryresult?.type) {
						notification[queryresult?.type]({
							message: queryresult.message,
							description: queryresult.description,
							duration: '5',
						})
					}
					setPromiseLoading(false)
				})
				.catch((e) => {
					console.log(e)
				})
		}
	}
	return (
		<div className='flex flex-col mb-6'>
			<div className='text-black-300 text-xs font-semibold mb-3'>Bilty Information</div>
			<div className='flex flex-col shadow-[0_0_4px_0_rgba(0,0,0,0.18)] bg-white rounded h-fit'>
				{record?.bilty?.length !== 0 ? (
					<>
						{record?.bilty?.map((bilty, index) => {
							const biltyTrackingStatus = bilty?.biltyStatus || 'notReceived'
							return (
								<div key={index} className={`px-4 py-3  ${index < record?.bilty?.length - 1 ? 'border-b border-dashed border-[rgba(0, 0, 0, 0.16)]' : ''}`}>
									<div className='font-semibold text-sm mb-1'>{bilty?.biltyNumber}</div>
									{segment !== 'LongHaul' && (
										<div className='text-xs text-black-200'>
											Linked with{' '}
											<span className='text-primary-800'>
												{SegmentsEnum[segment]?.documentTypeShort}
												{bilty?.documentNumber}
											</span>
										</div>
									)}
									<Form form={form} onValuesChange={handleChange}>
										{collection === 'biltyTracking' && (
											<div className={`flex justify-center items-center ${promiseLoading[bilty?._id] ? 'my-1' : 'mt-3'}`}>
												{promiseLoading[bilty?._id] ? (
													<Spin style={{ display: 'flex', justifyContent: 'center' }} size='medium' indicator={<LoadingOutlined style={{ fontSize: 24 }} />} />
												) : (
													<Form.Item className='w-full !m-0' key={[bilty?._id, 'biltyStatus']} name={[bilty?._id, 'biltyStatus']}>
														<Select
															suffixIcon={<Icon className={`${BiltyTrackingEnum[biltyTrackingStatus]?.textColor}`} size={0.9} path={mdiMenuDown} />}
															className={`TrackingSelectField rounded-[5px] font-bold text-sm ${biltyTrackingStatus !== 'notReceived' ? `WhiteSelectField` : ``} ${
																BiltyTrackingEnum[biltyTrackingStatus]?.background
															} ${BiltyTrackingEnum[biltyTrackingStatus]?.textColor}`}
															defaultValue={biltyTrackingStatus}
															value={biltyTrackingStatus}
															data-cy='biltyStatus'
														>
															{Object.values(BiltyTrackingEnum)
																?.filter((trackingStatus) => RBACSwitch(user?.userType, trackingStatus?.key, 'trackingOptions'))
																?.map((option, index) => (
																	<Select.Option key={index} value={option.key}>
																		<div className={`flex flex-row items-center justify-center`}>
																			<Icon path={BiltyTrackingEnum[option.key].icon} size={0.8} />
																			<p className='ml-3'>{option.text}</p>
																		</div>
																	</Select.Option>
																))}
														</Select>
													</Form.Item>
												)}
											</div>
										)}
									</Form>
								</div>
							)
						})}
					</>
				) : (
					<div className='flex flex-row items-center px-4 py-3'>
						<Icon className='mr-2 text-red-400' path={mdiAlertCircleOutline} size={0.7} />
						<div className='text-sm font-normal text-red-400'>Bilty Not Created</div>
					</div>
				)}
				<div className='flex flex-row h-10 px-4 items-center !rounded-b !bg-grey-500 border-t border-[rgba(0, 0, 0, 0.08)]'>
					<div className='flex flex-row items-center mr-3'>
						<Icon className='rounded-full p-1 bg-black-75 text-black-300 mr-2' path={mdiAccount} size={0.7} />
						<div className='text-xs font-normal text-black-300'>
							{record?.MatchedBy?.firstName} {record?.MatchedBy?.lastName}
						</div>
					</div>
					<div className='flex flex-row items-center'>
						<Icon className='rounded-full p-1 bg-black-75 text-black-300 mr-2' path={mdiClockTimeEightOutline} size={0.7} />
						<div className='text-xs font-normal text-black-300'>{momentDateDT(record?.driverMatchedAt)}</div>
					</div>
				</div>
			</div>
		</div>
	)
}

const ContainerInfoCard = (props) => {
	const { documentMappedOrders, segment } = props
	const initial = SegmentsEnum[segment].initial
	return (
		<div className='flex flex-col mb-6'>
			<div className='text-black-300 text-xs font-semibold mb-3'>{segment === 'LongHaul' ? 'Order' : 'Container'} Information</div>
			<div className='shadow-[0_0_4px_0_rgba(0,0,0,0.18)] rounded bg-white h-fit'>
				{Object.keys(documentMappedOrders)?.map((document, index) => (
					<div key={index} className='flex flex-col'>
						{segment !== 'LongHaul' && (
							<div
								className={`flex items-center px-4 h-10 bg-primary-50 border-b border-[rgba(0, 0, 0, 0.06)] text-primary-800 font-semibold text-sm ${
									index === 0 && 'rounded-t'
								}`}
							>
								{SegmentsEnum[segment]?.documentTypeShort} {document}
							</div>
						)}
						{documentMappedOrders[document]?.map((subOrder, index) => (
							<div
								key={index}
								className={`px-4 py-3  ${index < documentMappedOrders[document]?.length - 1 ? 'border-b border-dashed border-[rgba(0, 0, 0, 0.16)]' : ''}`}
							>
								<div className='flex flex-row items-center mb-2'>
									{segment !== 'LongHaul' && (
										<>
											<span className='mr-3 font-semibold'>
												<span className='text-sm font-normal text-black-300'>Container# </span>
												{subOrder?.containerNumber || '---'}
											</span>
											<span className='w-[1px] h-4 border border-[rgba(0, 0, 0, 0.12)] mr-3'></span>
										</>
									)}
									<span className='text-black-300 text-sm font-normal'>
										{initial}-{subOrder?.subOrderNumber}
									</span>
								</div>
								<div className='flex flex-row items-center'>
									<div className='flex flex-row items-center mr-3'>
										<Icon className='text-black-300 mr-2' path={mdiTextBoxOutline} size={0.7} />
										<span className='text-xs font-normal'>{ContainerDimensionsEnum[subOrder?.dimension || subOrder?.vehicleType]?.small}</span>
									</div>
									<div className='flex flex-row items-center'>
										<Icon className='text-black-300 mr-2' path={mdiPackageVariantClosed} size={0.7} />
										<span className='text-xs font-normal'>{subOrder?.freightWeight || subOrder?.shipmentInfo?.freightWeight} Tons</span>
									</div>
								</div>
								{(subOrder?.partialMatched || subOrder?.completeMatched) && (
									<div className='flex flex-row items-center w-fit text-xs bg-grey-200 rounded px-2 py-1 mt-2'>
										<Icon className='text-black-300 mr-1' path={subOrder?.partialMatched ? mdiVectorLink : mdiVectorUnion} size={0.6} />
										<span className='text-primary-800 font-bold mr-1'>{subOrder.movementCount}</span>
										<span className='text-black-300 font-medium mr-1'>out of</span>
										<span className='font-bold mr-1'>{subOrder.totalCount}</span>
										<span className='text-black-300 font-medium'>{subOrder?.movedUnits?.toLowerCase()}</span>
									</div>
								)}
							</div>
						))}
					</div>
				))}
			</div>
		</div>
	)
}

const DriverDetails = (props) => {
	const { record } = props
	return (
		<div className='flex flex-col mb-6'>
			<div className='text-black-300 text-xs font-semibold mb-3'>Driver & Broker Details</div>
			<div className='flex flex-row items-start shadow-[0_0_4px_0_rgba(0,0,0,0.18)] p-6 rounded bg-white h-fit'>
				<div className='flex flex-col mr-4'>
					<span className='text-black-300 text-sm font-normal mb-2'>Driver Name</span>
					<span className='text-black-300 text-sm font-normal mb-2'>Driver Phone Number</span>
					<span className='text-black-300 text-sm font-normal mb-2'>Vehicle Number</span>
					<span className='text-black-300 text-sm font-normal mb-2'>Broker</span>
					<span className='text-black-300 text-sm font-normal'>Cost</span>
				</div>
				<div className='flex flex-col justify-center'>
					<span className='text-sm font-semibold mb-2'>{record?.Driver?.name || '---'}</span>
					<span className='text-sm font-semibold mb-2'>{record?.Driver?.phoneNumber || '---'}</span>
					<span className='text-sm font-semibold mb-2'>{record?.Vehicle?.registrationNumber || '---'}</span>
					<span className='text-sm font-semibold mb-2'>
						{record?.Broker?.CompanyName
							? `${record?.Broker?.CompanyName}`
							: record?.Broker?.User?.firstName || record?.Broker?.User?.lastName
							? `${record?.Broker?.User?.firstName} ${record?.Broker?.User?.lastName}`
							: '---'}
					</span>
					<span className='text-sm font-semibold'>{record?.cost || '---'}</span>
				</div>
			</div>
		</div>
	)
}

const KantaDetails = (props) => {
	const { segment, tabStatus, record, documentMappedOrders, documnetMappedBilties, collection, navigator } = props
	return (
		<div className='flex flex-col'>
			<div className='text-black-300 text-xs font-semibold mb-3'>kanta Details</div>
			<div className='shadow-[0_0_4px_0_rgba(0,0,0,0.18)] rounded bg-white h-fit'>
				{Object.keys(documentMappedOrders)?.map((document, index) => {
					return (
						<div key={index} className='flex flex-col'>
							{segment !== 'LongHaul' && (
								<div
									className={`flex items-center px-4 h-10 bg-primary-50 border-b border-[rgba(0, 0, 0, 0.06)] text-primary-800 font-semibold text-sm ${
										index === 0 && 'rounded-t'
									}`}
								>
									{SegmentsEnum[segment]?.documentTypeShort} {document}
								</div>
							)}
							{documnetMappedBilties[document]?.initialKantaWeight ? (
								<div className='flex flex-row justify-between items-center px-4 py-3'>
									<div className='flex flex-row items-center justify-center'>
										<div className='flex flex-row items-center mr-3'>
											<Icon className='text-black-300 mr-1.5' path={mdiPackageVariantClosed} size={0.7} />
											<span className='text-xs font-normal'>{documnetMappedBilties[document]?.initialKantaWeight} Tons</span>
										</div>
										<div className='flex flex-row items-center mr-3'>
											<Icon className='text-black-300 mr-1.5' path={mdiCreditCardOutline} size={0.7} />
											<span className='text-xs font-normal'>Rs {documnetMappedBilties[document]?.initialKantaCharges || '---'}</span>
										</div>
										{documnetMappedBilties[document]?.initialKantaDocument?.[0] && (
											<div className='flex flex-row items-center'>
												<a
													target='_blank'
													rel='noreferrer'
													onClick={(e) => e.stopPropagation()}
													className='text-black-300 text-sm font-semibold'
													href={documnetMappedBilties[document]?.initialKantaDocument?.[0]}
												>
													<Icon className='text-black-300 mr-2' path={mdiReceipt} size={0.7} />
												</a>
											</div>
										)}
									</div>
									{collection !== 'biltyTracking' && (
										<div
											className='flex flex-row items-center mr-3'
											onClick={(e) => {
												e.stopPropagation()
												navigator(`/biltyManagement/${segment}/${tabStatus}/job/${record?._id}/sideDrawer/missingKanta/${documnetMappedBilties[document]?.biltyNumber}`)
											}}
										>
											<Icon className='text-red-400' path={mdiFileEditOutline} size={0.7} />
										</div>
									)}
								</div>
							) : (
								<div className='flex flex-col px-4 py-3'>
									<div className='flex flex-row items-center mb-0.5'>
										<Icon className='mr-2 text-red-400' path={mdiAlertCircleOutline} size={0.7} />
										<div className='text-sm font-normal text-red-400'>Missing Kanta Weight</div>
									</div>
									{collection !== 'biltyTracking' && (
										<div
											className='ml-6 cursor-pointer text-primary-800 font-medium text-sm underline underline-offset-2'
											onClick={(e) => {
												e.stopPropagation()
												navigator(`/biltyManagement/${segment}/${tabStatus}/job/${record?._id}/sideDrawer/missingKanta/${documnetMappedBilties[document]?.biltyNumber}`)
											}}
										>
											Add Kanta
										</div>
									)}
								</div>
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}

const JobSideDrawer = () => {
	const {
		setChangedValues,
		props: {
			segment,
			isLot,
			collection,
			data: { record, job },
		},
	} = useContext(LevelContext)
	const { orderType, tabStatus } = useParams()
	const navigator = useNavigate()

	const data = isLot ? job : record
	const documentMappedOrders = documentOrdersMapFunction(data?.subOrders)
	const documnetMappedBilties = documentBiltiesObjectFunction(data?.bilty)

	return (
		<>
			<Drawer
				placement='right'
				open={true}
				closable={false}
				closeIcon={false}
				width={400}
				title={
					<div className='flex flex-col w-full'>
						<DrawerHeader record={data} tabStatus={tabStatus} orderType={orderType} collection={collection} navigator={navigator} />
						<RouteCollapse record={data} />
					</div>
				}
				onClose={() => navigator(-1)}
			>
				<div className='flex flex-col w-full'>
					<div className='p-6  bg-secondaryBackground'>
						<BiltyInfoCard key='BiltyInfoCard' record={data} setChangedValues={setChangedValues} segment={segment} collection={collection} />
						<ContainerInfoCard key='ContainerInfoCard' documentMappedOrders={documentMappedOrders} segment={segment} />
						<DriverDetails key='DriverDetails' record={data} />
						{data?.bilty?.length !== 0 && data?.bilty?.some((bilty) => bilty?.biltyCreatedAt) && (
							<KantaDetails
								key='KantaDetails'
								record={data}
								navigator={navigator}
								tabStatus={tabStatus}
								segment={segment}
								collection={collection}
								documentMappedOrders={documentMappedOrders}
								documnetMappedBilties={documnetMappedBilties}
							/>
						)}
					</div>
				</div>
			</Drawer>
			<Outlet />
		</>
	)
}

export default JobSideDrawer
